/**
 * useInstallPrompt Hook
 * 
 * Captures the beforeinstallprompt event and provides
 * a way to trigger the native install prompt.
 */

import { useEffect, useState, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptState {
  canInstall: boolean;
  isInstalled: boolean;
  isIOS: boolean;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<InstallPromptState>({
    canInstall: false,
    isInstalled: false,
    isIOS: false,
  });

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // Check if iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    setState((prev) => ({
      ...prev,
      isInstalled: isStandalone,
      isIOS,
    }));

    if (isStandalone) {
      console.log('[Install] App is already installed');
      return;
    }

    // Capture the install prompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setState((prev) => ({ ...prev, canInstall: true }));
      console.log('[Install] Install prompt captured');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null);
      setState((prev) => ({ ...prev, canInstall: false, isInstalled: true }));
      console.log('[Install] App installed successfully');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.log('[Install] No install prompt available');
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for user choice
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log('[Install] User choice:', outcome);
      
      // Clear the prompt regardless of outcome
      setDeferredPrompt(null);
      setState((prev) => ({ ...prev, canInstall: false }));
      
      return outcome === 'accepted';
    } catch (error) {
      console.error('[Install] Error showing prompt:', error);
      return false;
    }
  }, [deferredPrompt]);

  const dismissPrompt = useCallback(() => {
    // Store dismissal in localStorage so we don't pester
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setDeferredPrompt(null);
    setState((prev) => ({ ...prev, canInstall: false }));
  }, []);

  // Check if user recently dismissed
  const wasDismissedRecently = useCallback((): boolean => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (!dismissed) return false;
    
    const dismissedTime = parseInt(dismissed, 10);
    const dayInMs = 24 * 60 * 60 * 1000;
    const weekAgo = Date.now() - (7 * dayInMs);
    
    // Don't show again for a week after dismissal
    return dismissedTime > weekAgo;
  }, []);

  return {
    ...state,
    canInstall: state.canInstall && !wasDismissedRecently(),
    promptInstall,
    dismissPrompt,
  };
}

export default useInstallPrompt;
